import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

/**
 * InstallAppButton — Sidebar menu item to install the PWA.
 *
 * Behavior:
 *   - Hidden if already installed (running in standalone mode).
 *   - Hidden on desktop (installation only makes sense on mobile/tablet).
 *   - Android/Chrome/Edge/Samsung Internet → uses native beforeinstallprompt.
 *   - iOS Safari → shows a modal with step-by-step instructions
 *     (Apple does not allow programmatic install).
 */
const InstallAppButton = ({ collapsed = false }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Already installed? → hide
    const isStandalone =
      window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = window.navigator.standalone === true;
    if (isStandalone || isIOSStandalone) {
      setIsVisible(false);
      return;
    }

    const ua = window.navigator.userAgent || '';
    const isMobile = /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(ua);
    if (!isMobile) {
      setIsVisible(false);
      return;
    }

    const iosDetected = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
    setIsIOS(iosDetected);

    if (iosDetected) {
      // iOS Safari cannot fire beforeinstallprompt. We show the guide modal.
      setIsVisible(true);
      return;
    }

    // Android/Chrome path: listen for the native install prompt event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Also listen for successful install to hide the button
    const installedHandler = () => {
      setIsVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleClick = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice && choice.outcome === 'accepted') {
          setIsVisible(false);
        }
        setDeferredPrompt(null);
      } catch (err) {
        console.error('Install prompt error:', err);
      }
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="install-app-menu-btn"
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-md hover:shadow-lg transition-all duration-200 ${collapsed ? 'justify-center' : ''}`}
        title="Instalar IBA Connect en tu celular"
      >
        <i className="fas fa-mobile-alt text-lg animate-pulse"></i>
        {!collapsed && (
          <>
            <span className="flex-1 text-left">Instalar App</span>
            <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full">Nuevo</span>
          </>
        )}
      </button>

      {/* iOS instrucciones */}
      <Dialog open={showIOSGuide} onOpenChange={setShowIOSGuide}>
        <DialogContent className="max-w-md" data-testid="ios-install-guide">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fab fa-apple text-gray-800"></i>
              Instalar en tu iPhone / iPad
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-gray-700">
              Safari no permite instalar la app con un botón directo. Seguí estos 3 pasos:
            </p>
            <ol className="space-y-3">
              <li className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded p-3">
                <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    Tocá el botón <b>Compartir</b>{' '}
                    <i className="fas fa-share-square text-blue-600 mx-1"></i>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Está en la barra inferior de Safari (el cuadrado con flecha hacia arriba).</p>
                </div>
              </li>
              <li className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded p-3">
                <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">2</span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    Deslizá hacia abajo y tocá{' '}
                    <b>&quot;Agregar a inicio&quot;</b>{' '}
                    <i className="fas fa-plus-square text-gray-700 mx-1"></i>
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded p-3">
                <span className="flex-shrink-0 w-7 h-7 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">3</span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    Tocá <b>&quot;Agregar&quot;</b> arriba a la derecha.
                  </p>
                  <p className="text-xs text-gray-500 mt-1">¡Listo! El ícono de IBA Connect aparecerá en la pantalla de inicio como una app.</p>
                </div>
              </li>
            </ol>
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
              <i className="fas fa-info-circle mr-1"></i>
              Importante: tenés que abrir esta página en <b>Safari</b> (no en Chrome, ni desde otra app). Si estás en otra app, tocá el menú &quot;⋯&quot; → &quot;Abrir en Safari&quot;.
            </div>
            <Button className="w-full" onClick={() => setShowIOSGuide(false)}>
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallAppButton;
